/**
 * Static ticker → long-name lookup.
 *
 * Used as a fallback when a broker payload doesn't carry a separate
 * company / ETF name. Alpaca's `/trading-history` rows, for example,
 * carry the symbol but no `company` field — without this lookup the
 * Order detail drawer would render "IT · IT" instead of the
 * informative "IT · Gartner Inc.".
 *
 * Not exhaustive. When a ticker isn't in the table the caller falls
 * back to the symbol itself; the UI then suppresses the duplicate
 * "IT · IT" automatically (see `OrdersSimpleView` Ticker row), so
 * an absent entry just hides the second line gracefully rather than
 * showing junk.
 *
 * Keep entries in roughly Yahoo-Finance casing — that's the canonical
 * form most users expect, and matches what real broker statements
 * print.
 */
export const TICKER_LONG_NAMES: Record<string, string> = {
  // ---- Index / broad-market ETFs ----
  SPY: "SPDR S&P 500 ETF Trust",
  IVV: "iShares Core S&P 500 ETF",
  VOO: "Vanguard S&P 500 ETF",
  VTI: "Vanguard Total Stock Market ETF",
  QQQ: "Invesco QQQ Trust",
  QQQM: "Invesco NASDAQ 100 ETF",
  DIA: "SPDR Dow Jones Industrial Average ETF",
  IWM: "iShares Russell 2000 ETF",
  // ---- Treasury / bond ETFs ----
  TLT: "iShares 20+ Year Treasury Bond ETF",
  IEF: "iShares 7-10 Year Treasury Bond ETF",
  SHY: "iShares 1-3 Year Treasury Bond ETF",
  SGOV: "iShares 0-3 Month Treasury Bond ETF",
  BND: "Vanguard Total Bond Market ETF",
  AGG: "iShares Core US Aggregate Bond ETF",
  // ---- Commodity / volatility ETFs ----
  GLD: "SPDR Gold Shares",
  SLV: "iShares Silver Trust",
  USO: "United States Oil Fund",
  UVXY: "ProShares Ultra VIX Short-Term Futures",
  VXX: "iPath Series B S&P 500 VIX Short-Term Futures",
  // ---- Leveraged / inverse ----
  TQQQ: "ProShares UltraPro QQQ",
  SQQQ: "ProShares UltraPro Short QQQ",
  SPXL: "Direxion Daily S&P 500 Bull 3X Shares",
  SPXS: "Direxion Daily S&P 500 Bear 3X Shares",
  TMF: "Direxion Daily 20+ Year Treasury Bull 3X Shares",
  TMV: "Direxion Daily 20+ Year Treasury Bear 3X Shares",

  // ---- Mega-cap US equities ----
  AAPL: "Apple Inc.",
  MSFT: "Microsoft Corporation",
  GOOGL: "Alphabet Inc. Class A",
  GOOG: "Alphabet Inc. Class C",
  AMZN: "Amazon.com Inc.",
  NVDA: "NVIDIA Corporation",
  META: "Meta Platforms Inc.",
  TSLA: "Tesla Inc.",
  BRK_A: "Berkshire Hathaway Inc. Class A",
  BRK_B: "Berkshire Hathaway Inc. Class B",
  AVGO: "Broadcom Inc.",
  ORCL: "Oracle Corporation",
  // ---- Financials ----
  JPM: "JPMorgan Chase & Co.",
  BAC: "Bank of America Corporation",
  WFC: "Wells Fargo & Company",
  GS: "Goldman Sachs Group, Inc.",
  MS: "Morgan Stanley",
  C: "Citigroup Inc.",
  BLK: "BlackRock Inc.",
  SCHW: "The Charles Schwab Corporation",
  AXP: "American Express Company",
  V: "Visa Inc.",
  MA: "Mastercard Incorporated",
  // ---- Healthcare ----
  UNH: "UnitedHealth Group Incorporated",
  JNJ: "Johnson & Johnson",
  LLY: "Eli Lilly and Company",
  PFE: "Pfizer Inc.",
  MRK: "Merck & Co., Inc.",
  ABBV: "AbbVie Inc.",
  TMO: "Thermo Fisher Scientific Inc.",
  // ---- Consumer ----
  KO: "The Coca-Cola Company",
  PEP: "PepsiCo, Inc.",
  WMT: "Walmart Inc.",
  COST: "Costco Wholesale Corporation",
  HD: "The Home Depot, Inc.",
  LOW: "Lowe's Companies, Inc.",
  NKE: "NIKE, Inc.",
  MCD: "McDonald's Corporation",
  SBUX: "Starbucks Corporation",
  PG: "The Procter & Gamble Company",
  // ---- Industrials / energy ----
  XOM: "Exxon Mobil Corporation",
  CVX: "Chevron Corporation",
  CAT: "Caterpillar Inc.",
  DE: "Deere & Company",
  GE: "GE Aerospace",
  HON: "Honeywell International Inc.",
  WM: "Waste Management, Inc.",
  FAST: "Fastenal Company",
  // ---- Tech / software ----
  CRM: "Salesforce, Inc.",
  ADBE: "Adobe Inc.",
  INTC: "Intel Corporation",
  AMD: "Advanced Micro Devices, Inc.",
  QCOM: "Qualcomm Incorporated",
  CSCO: "Cisco Systems, Inc.",
  NOW: "ServiceNow, Inc.",
  INTU: "Intuit Inc.",
  IT: "Gartner, Inc.",
  VEEV: "Veeva Systems Inc.",
  PANW: "Palo Alto Networks, Inc.",
  CRWD: "CrowdStrike Holdings, Inc.",
  SNOW: "Snowflake Inc.",
  // ---- Misc commonly-held ----
  DIS: "The Walt Disney Company",
  NFLX: "Netflix, Inc.",
  T: "AT&T Inc.",
  VZ: "Verizon Communications Inc.",
  PYPL: "PayPal Holdings, Inc.",
  SQ: "Block, Inc.",
  UBER: "Uber Technologies, Inc.",
  SHOP: "Shopify Inc.",
  PLTR: "Palantir Technologies Inc.",
  COIN: "Coinbase Global, Inc.",

  // ---- Backfill from Sergey's real-account orders (2026-04-26) ----
  // Tickers that surfaced as ASSET / ASSET duplicates in the Orders
  // table because the broker payload doesn't carry a separate
  // company name. Adding here resolves them on next render.
  BDX: "Becton, Dickinson and Company",
  NWSA: "News Corporation Class A",
  GPI: "Group 1 Automotive, Inc.",
  WMB: "The Williams Companies, Inc.",
  CACI: "CACI International Inc",
  DRS: "Leonardo DRS, Inc.",
  // ---- Other broadly-traded names worth pre-populating ----
  ABT: "Abbott Laboratories",
  ACN: "Accenture plc",
  AMAT: "Applied Materials, Inc.",
  AMGN: "Amgen Inc.",
  AMT: "American Tower Corporation",
  BA: "The Boeing Company",
  BMY: "Bristol-Myers Squibb Company",
  BX: "Blackstone Inc.",
  CMCSA: "Comcast Corporation",
  CME: "CME Group Inc.",
  COP: "ConocoPhillips",
  DHR: "Danaher Corporation",
  DUK: "Duke Energy Corporation",
  EOG: "EOG Resources, Inc.",
  GILD: "Gilead Sciences, Inc.",
  IBM: "International Business Machines Corporation",
  ISRG: "Intuitive Surgical, Inc.",
  LIN: "Linde plc",
  LMT: "Lockheed Martin Corporation",
  MDT: "Medtronic plc",
  MMM: "3M Company",
  MO: "Altria Group, Inc.",
  NEE: "NextEra Energy, Inc.",
  NOC: "Northrop Grumman Corporation",
  PLD: "Prologis, Inc.",
  PM: "Philip Morris International Inc.",
  RTX: "RTX Corporation",
  SO: "The Southern Company",
  SPGI: "S&P Global Inc.",
  TGT: "Target Corporation",
  TMUS: "T-Mobile US, Inc.",
  TXN: "Texas Instruments Incorporated",
  UPS: "United Parcel Service, Inc.",
  USB: "U.S. Bancorp",
  // ETFs commonly held
  EFA: "iShares MSCI EAFE ETF",
  EEM: "iShares MSCI Emerging Markets ETF",
  VEA: "Vanguard FTSE Developed Markets ETF",
  VWO: "Vanguard FTSE Emerging Markets ETF",
  VNQ: "Vanguard Real Estate ETF",
  XLF: "Financial Select Sector SPDR Fund",
  XLE: "Energy Select Sector SPDR Fund",
  XLK: "Technology Select Sector SPDR Fund",
  XLY: "Consumer Discretionary Select Sector SPDR Fund",
  XLP: "Consumer Staples Select Sector SPDR Fund",
  XLV: "Health Care Select Sector SPDR Fund",
  XLI: "Industrial Select Sector SPDR Fund",
  XLU: "Utilities Select Sector SPDR Fund",
  XLB: "Materials Select Sector SPDR Fund",
  XLRE: "Real Estate Select Sector SPDR Fund",
  XLC: "Communication Services Select Sector SPDR Fund",
};

/**
 * Resolve a ticker to its long name. Case-insensitive lookup; returns
 * the symbol unchanged when no entry exists. Use this anywhere the UI
 * surfaces an asset name and the broker payload may not have one.
 */
export function resolveAssetName(symbol: string): string {
  if (!symbol) return symbol;
  const key = symbol.toUpperCase();
  return TICKER_LONG_NAMES[key] ?? symbol;
}
